/**
 * `useFileDownload` — hand the lawyer the ORIGINAL bytes of a workspace
 * file, fetching them only when they actually ask.
 *
 * Why on-demand and not just `useFileBytes`: the unsupported-binary
 * viewer exists precisely so that opening a `.xlsx` / `.pptx` / archive
 * does NOT pull its bytes across the wire (the original
 * `UnsupportedViewer` note: "The bytes are NEVER fetched here, so no
 * network/payload cost for opening a binary blob"). Rendering a download
 * button must not quietly undo that — a lawyer clicking through a folder
 * of large binaries would pay a full read per file for a control they
 * never press. So the read is armed by the click.
 *
 * Once armed the bytes stay in react-query's cache, so a second download
 * of the same file is instant and does not re-read.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useFileBytes } from "./useFileBytes";

export interface UseFileDownloadResult {
  /** Fire the download. Fetches first if the bytes aren't loaded yet. */
  request: () => void;
  /** Bytes are in flight because the user asked for them. */
  pending: boolean;
  isError: boolean;
  error: Error | null;
}

function saveBlobUrl(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
}

export function useFileDownload(
  path: string | null,
  filename: string,
): UseFileDownloadResult {
  const [armed, setArmed] = useState(false);
  // Stays armed after the first download so the cached bytes survive; the
  // blob URL must NOT be revoked out from under an in-progress save.
  const { url, isPending, isError, error } = useFileBytes(armed ? path : null);
  const wantSaveRef = useRef(false);

  useEffect(() => {
    if (!wantSaveRef.current || !url) return;
    wantSaveRef.current = false;
    saveBlobUrl(url, filename);
  }, [url, filename]);

  // A failed read must not leave the click silently pending forever —
  // drop the intent so the control returns to an actionable state and the
  // caller can surface `isError`.
  useEffect(() => {
    if (isError) wantSaveRef.current = false;
  }, [isError]);

  const request = useCallback(() => {
    if (url) {
      saveBlobUrl(url, filename);
      return;
    }
    wantSaveRef.current = true;
    setArmed(true);
  }, [url, filename]);

  return {
    request,
    pending: armed && isPending,
    isError,
    error: (error as Error | null) ?? null,
  };
}
